import {getAudioContext} from "../audioContext";
import {soundAssets} from "../../config/config";
import InstrumentSamplePlayer from "../synthesizer/InstrumentSamplePlayer";
import {timeSignatureBeatVolumesPerMeasureMap} from "./timeSignatureBeatVolumesPerMeasureMap";
import Note from "../../models/soundslice/Note";
import NotePath from "../../models/soundslice/NotePath";
import NotesPlaybackScheduler from "../playbackOrchestrator/NotesPlaybackScheduler";
import {ISchedulePlayNote} from "../synthesizer/ISchedulePlayNote";
import Segment from "../../models/view/lessonPage/Segment";
import Measure from "../../models/view/lessonPage/Measure";
import {subscribeToSettingsEvents} from "../eventBus/settings/settingsEventBus";
import {SettingsEventTypes} from "../eventBus/settings/SettingsEventTypes";
import {logger} from "../log/logger";
import {IVolumes} from "../../redux/types/IVolumes";

const midiForHihat = 0;
/**
 * The metronome plays tick patterns for various time signatures.
 * It currently assumes that each bar is evenly divided, which is not true for all music pieces, but true for Playground Sessions pieces.
 */
class Metronome implements ISchedulePlayNote{
  //conveniently control volume of the metronome
  gainNode: GainNode;
  volume: number = 1;
  //how we play the hihat sound
  hihatSamplePlayer: InstrumentSamplePlayer;
  //scheduler responsible for math, worker threads, batching, etc of notes.
  notesPlaybackScheduler = new NotesPlaybackScheduler();
  constructor(){
    this.createAndLoadWebAudioNodes();
    this.#listenForSettingsEvents();
  }

  #listenForSettingsEvents = () => {
    subscribeToSettingsEvents((message: SettingsEventTypes)=> {
      this.#routeSettingsEvent(message);
    });
  }

  #routeSettingsEvent = (message: SettingsEventTypes) => {
    switch (message.type) {
      case "audioContextSampleRateChanged":
        this.#onAudioContextSampleRate_changed();
        return;
      case "volumesChanged":
        this.#onVolumes_changed(message.volumes);
        return;
    }
  }

  #onAudioContextSampleRate_changed = () => {
    this.createAndLoadWebAudioNodes();
  }

  #onVolumes_changed(volumes: IVolumes): void {
    const adjustedVolume = volumes.isMetronomeMuted ? 0 : volumes.metronome;
    this.setVolume(adjustedVolume);
  }

  createAndLoadWebAudioNodes(){
    const context = getAudioContext();
    this.gainNode = context.createGain();
    this.hihatSamplePlayer = buildHiHatInstrumentSamplePlayer(soundAssets.metronome.sampleFileNames.loudness4, this.gainNode);
    this.gainNode.connect(context.destination);
    void this.hihatSamplePlayer.load();
  }
  setVolume(volume: number){
    this.gainNode.gain.value = volume;
  }

  /**
   * Immediately start playing metronome.
   * @param speed - 1 for 100%, .5 for 50%, etc
   * @param segment - we reference the measures and durationSeconds to create and play metronome notes.
   * @param barDurationSeconds - each bar is expected to be the same duration, in seconds.  Although this isn't true for complicated pieces of music, it is true for PS music.
   * @param seekOffsetSeconds - where the cursor position is, in seconds. i.e. at what position in the song should we start playback at.
   * @param loopEndOffsetSeconds - -1 if not applicable.  We use this so that 2 ticks don't intermittently occur at the end of a loop because the ssSeek fires right after InstrumentSamplePlayer scheduled note fires.
   */
  play({speed, segment, seekOffsetSeconds, loopEndOffsetSeconds, getAudioCurrentTime}: {speed: number; segment: Segment; seekOffsetSeconds: number; loopEndOffsetSeconds: number, getAudioCurrentTime?: () => number }){
    if(!segment.measures || segment.measures.length <= 0) {
      return logger.lessonAudio.error({message: `can't play metronome without segment measure data` + segment});
    }

    logger.metronome.info({
      message: "play",
      data: [
        { key: "started actual", value: getAudioContext().currentTime },
        { key: "getAudioCurrentTime", value: getAudioCurrentTime?.() },
      ],
    })

    this.stop();
    const relativeSeekOffsetSeconds = seekOffsetSeconds;
    const hihatNotesForEntireSong = this.createHihatNotesForMeasures(segment.measures);
    this.notesPlaybackScheduler.scheduleNotesPlayback({
      notes: hihatNotesForEntireSong,
      speed,
      seekOffsetSeconds: relativeSeekOffsetSeconds,
      synthesizer: this,
      loopEndOffsetSeconds,
      getAudioCurrentTime
    });
  }

  /**
   * Evaluates each measure's time signature and tempo in order to create appropriate hi hat note ticks/sounds.
   * @param measures - measures to create hihat ticks for.
   */
  createHihatNotesForMeasures(measures: Measure[]){
    //iterate over each measure and create hi hat notes for them.  Keep track of the starting offset
    const {notes} = measures.reduce(({notes, measureStartOffsetSeconds}, measure, measureIndex) => {
      const {notes: measureNotes, measureDurationSeconds} = createHihatNotesForMeasure(measure, measureIndex, measureStartOffsetSeconds);
      return {notes: [...notes, ...measureNotes], measureStartOffsetSeconds: measureStartOffsetSeconds + measureDurationSeconds};
    }, {notes: [] as Note[], measureStartOffsetSeconds: 0});
    return notes;
  }
  /**
   * Stop playing the metronome.
   */
  stop(){
    this.notesPlaybackScheduler.cancelAllScheduledNotesPlayback();
  }

  /**
   * Implementation of ISchedulePlayNote allows the NotesPlaybackScheduler to be passed either metronome or synthesizer.
   * @param startAtContextRelativeSeconds - audioContext.currentTime based time to start the note at.
   * @param durationInSeconds - how long to play the note for
   * @param velocity - how loud to play the note
   * @param midi - we ignore this since we only have one sample to play.
   */
  schedulePlayNote({startAtContextRelativeSeconds, durationInSeconds, velocity, midi}: {midi: number; velocity: number; startAtContextRelativeSeconds: number; durationInSeconds: number}){
    const volume = convertVelocityToVolume(velocity);
    this.hihatSamplePlayer.schedulePlay({volume, startAtContextRelativeSeconds, durationInSeconds});
  }

  /**
   * Implementation of ISchedulePlayNote allows the NotesPlaybackScheduler to be passed either metronome or synthesizer.
   * Stops all samples from being played.
   */
  stopAllScheduledPlays(){
    this.hihatSamplePlayer.stopAllScheduledPlays();
  }
}

function createHihatNotesForMeasure(measure: Measure, measureIndex: number, offset: number){
  const {beats, beatType, durationSeconds: measureDurationSeconds} = measure;
  //get the array of hithat  volumes, 1 volume for each beat, so that we can create notes.
  const beatVolumesPerBar = getBeatVolumesPerMeasureForTimeSignature(beats, beatType);
  //determine how far apart, in seconds, the hi hat notes should be spaced.
  const offsetIncrementSeconds = measureDurationSeconds / beatVolumesPerBar.length;
  //iterate over each volume and create a note for it.
  const {notes} = beatVolumesPerBar.reduce(({notes, offset, beatIndex}, volume) => {
    const path = new NotePath({barIndex: measureIndex, beatIndex, trackIndex: 0, voiceIndex: 0, noteIndex: 0});
    const note = new Note({offset, duration: .5, tieEnd: false, midi: midiForHihat, path, velocity: convertVolumeToVelocity(volume), clef: 'bass'});
    return {notes: [...notes, note] as Note[], offset: offset + offsetIncrementSeconds, beatIndex: beatIndex + 1};
  }, {notes: [] as Note[], offset, beatIndex: 0});
  return {notes, measureDurationSeconds};
}

/**
 * Converts web audio volume format to midi velocity.
 * @param volume - e.g. 1, .7, .5
 */
function convertVolumeToVelocity(volume: number){
  return volume * 127;
}

/**
 * Converts midi velocity to web audio volume format (e.g. 1, .5, etc)
 * @param velocity - midi velocity e.g. 127, 100, etc
 */
function convertVelocityToVolume(velocity?: number){
  return velocity !== undefined ? velocity / 127 : 1;
}

/**
 * Look up and return the tick pattern & volumes for the given time signature.
 * @param beatsPerMeasure - the 3 of 3/4
 * @param beatType - the 4 of 3/4. where 4 is a quarter note.
 */
function getBeatVolumesPerMeasureForTimeSignature(beatsPerMeasure: number, beatType: number): number[]{
  const key = `${beatsPerMeasure}_${beatType}`;
  return timeSignatureBeatVolumesPerMeasureMap[key] || timeSignatureBeatVolumesPerMeasureMap["4_4"];
}

/**
 * Helper function for constructing our web audio source node wrapper.
 * @param assetFileName - e.g. hihat_short_1.wav
 * @param destination
 */
function buildHiHatInstrumentSamplePlayer(assetFileName: string, destination: AudioNode){
  return new InstrumentSamplePlayer({url: `${soundAssets.baseUrl}${soundAssets.metronome.relativePath}${assetFileName}`, destination, });
}

/**
 * Note offsets (start) and durations are not changed when playback speed is changed, so we need to calculate new times based on the speed.
 * @param note - note with offset (when note should start) and duration.
 * @param speed - e.g. 1 for 100%, 1.1 for 110%, .9 for 90%, etc
 */
function getSpeedAdjustedOffsetAndDurationInSeconds(note: Note, speed: number){
  const {offset: offsetSeconds, duration, durationWithTie} = note;
  //notes that belong to a tie should be played as if they were one note.  Since duration doesn't represent the combined notes, use the durationWithTie prop, which we calculate ourselves, as the duration which the note should be played.
  const durationSeconds = durationWithTie !== undefined ? durationWithTie : duration;
  const speedAdjustedOffsetSeconds = offsetSeconds / speed;
  const speedAdjustedDurationSeconds = durationSeconds / speed;
  return {speedAdjustedOffsetSeconds, speedAdjustedDurationSeconds};
}

const metronome = new Metronome();
export default metronome;
